import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingCompany, ELanguage } from '@if/core/types'
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { AsyncActionButton } from '@src/components'
import Breadcrumb from '@src/components/Breadcrumb'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { useMerchantById, useUpdateBanner } from '@src/data/api/merchants-api/merchants-api'
import { BannerDto, BannerSchema } from '@src/data/types/BannerSchema'
import { EMerchantReportFrequency } from '@src/data/types/Constants'
import { AxiosError } from 'axios'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import BannerInformationPanel from './components/BannerInformationPanel'
import BannerPropertiesPanel from './components/BannerPropertiesPanel'
import BannerReportConfigPanel from './components/BannerReportConfigPanel'

const EditBannerPage = () => {
  const { id } = useParams()
  const [banner, isLoading] = useMerchantById(id ?? '')
  const [updateBanner, isUpdatingBanner, resetUpdateBanner, updateBannerError] = useUpdateBanner()
  const [featureFlags] = useFeatureFlags()

  const { t } = useTranslation()
  const navigate = useNavigate()

  const form = useForm<BannerDto>({
    resolver: yupResolver(BannerSchema),
    defaultValues: {
      id: banner?.id ?? '',
      name: banner?.name ?? '',
      sourceSystem: banner?.sourceSystem ?? '',
      serviceCategory: banner?.serviceCategory ?? '',
      email: banner?.email ?? '',
      phone: banner?.phone ?? '',
      website: banner?.website ?? '',
      languageId: banner?.languageId ?? ELanguage.English,
      address: banner?.address ?? '',
      city: banner?.city ?? '',
      zipCode: banner?.zipCode ?? '',
      province: banner?.province ?? '',
      reportGenerationFrequency: banner?.reportGenerationFrequency ?? EMerchantReportFrequency.never,
      reportRecipientsEmailList: banner?.reportRecipientsEmailList ?? [],
      worksheetByMerchant: banner?.worksheetByMerchant ?? false,
    },
  })
  const {
    control,
    formState: { isDirty: isFormDirty },
  } = form

  const isSubmitDisabled = isUpdatingBanner || !isFormDirty

  const onSubmit = async (data: BannerDto) => {
    await updateBanner(data).then(() => {
      resetUpdateBanner()
    })
    navigate(`/banner/${data.id}/view?financingCompanyId=${banner?.financingCompanyId}`, { replace: true })
  }

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    {
      path: `/banner/${banner?.id}/view`,
      label: t('breadcrumbs.banner').concat(` : ${banner?.name}`),
    },
    {
      path: `/banner/${banner?.id}/edit?financingCompanyId=${banner?.financingCompanyId}`,
      label: t('breadcrumbs.edit'),
    },
  ]

  if (isLoading) {
    return (
      <Box>
        <Breadcrumb trees={breadCrumbs} />
        <CircularProgress />
      </Box>
    )
  }
  if (!banner) {
    return <div>merchant not found</div>
  }
  if (banner.financingCompanyId === EFinancingCompany.Iceberg) navigate(`/banner/${id}/view`, { replace: true })

  return (
    <Box>
      <Breadcrumb trees={breadCrumbs} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom>
          {t('banner.edit')}
        </Typography>
        <Typography variant="body1" color="text.secondary" fontSize={'1.5rem'}>
          {banner.name}
        </Typography>
      </Box>
      <FormProvider {...form}>
        {updateBannerError !== null && (
          <Alert severity="error">
            {t(
              `common.errors.${(updateBannerError as AxiosError<{ message: string }>).response?.data?.message || 'unknown'}`,
              'An error occurred',
            )}
          </Alert>
        )}
        <Stack gap={2} component="form" onSubmit={form.handleSubmit(onSubmit)}>
          <BannerInformationPanel control={control} />
          <BannerReportConfigPanel control={control} />
          {featureFlags?.useHybridFlowWorksheetByMerchant && <BannerPropertiesPanel control={control} />}
          <Stack direction="row" justifyContent="end" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              href={`/banner/${banner.id}/view?financingCompanyId=${banner.financingCompanyId}`}
            >
              {t('common.back')}
            </Button>
            <AsyncActionButton
              variant="contained"
              color="primary"
              type="submit"
              isPending={isUpdatingBanner}
              disabled={isSubmitDisabled}
            >
              {t('common.save')}
            </AsyncActionButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Box>
  )
}

export default EditBannerPage
